import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { 
  Heart, Users, MapPin, Clock, Calendar, CheckCircle, ArrowRight, 
  Loader2, Search, Building2, Star, Award, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const COMPETENCIAS = [
  'Acompanhamento de Idosos',
  'Apoio Administrativo',
  'Apoio Escolar',
  'Artes e Cultura',
  'Cuidados Infantis',
  'Desporto',
  'Distribuição de Alimentos',
  'Formação e Ensino',
  'Gestão de Eventos',
  'Informática',
  'Línguas Estrangeiras',
  'Manutenção e Bricolage',
  'Marketing e Comunicação',
  'Saúde e Primeiros Socorros',
  'Transporte',
  'Outro'
];

const DISPONIBILIDADE = [
  { id: 'manha', label: 'Manhã (9h-13h)' },
  { id: 'tarde', label: 'Tarde (14h-18h)' },
  { id: 'noite', label: 'Noite (após 18h)' },
  { id: 'fds', label: 'Fins de Semana' }
];

const TESTIMONIALS = [
  {
    name: 'Maria Santos',
    role: 'Voluntária há 2 anos',
    quote: 'Ser voluntária mudou a minha vida. A sensação de fazer a diferença na vida de alguém não tem preço.',
    image: null
  },
  {
    name: 'João Pereira',
    role: 'Voluntário desde 2021',
    quote: 'Encontrei uma segunda família na instituição onde faço voluntariado. Recomendo a todos!',
    image: null
  },
  {
    name: 'Ana Costa',
    role: 'Voluntária corporativa',
    quote: 'A minha empresa incentiva o voluntariado e tenho conseguido ajudar regularmente.',
    image: null
  }
];

export default function PublicVoluntariadoPage() {
  const [loading, setLoading] = useState(true);
  const [missoes, setMissoes] = useState([]);
  const [distritos, setDistritos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroDistrito, setFiltroDistrito] = useState('all');
  const [filtroTipo, setFiltroTipo] = useState('all');
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [selectedMissao, setSelectedMissao] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    distrito_id: '',
    competencias: [],
    disponibilidade: [],
    motivacao: '',
    aceita_termos: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [missoesRes, distritosRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/missoes`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/distritos`)
      ]);
      
      if (missoesRes.ok) setMissoes(await missoesRes.json());
      if (distritosRes.ok) setDistritos(await distritosRes.json());
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.email || !formData.telefone) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    if (!formData.aceita_termos) {
      toast.error('Deve aceitar os termos e condições');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/voluntario/candidatura`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          missao_id: selectedMissao?.id || null
        })
      });
      
      if (response.ok) {
        toast.success('Candidatura submetida com sucesso! Entraremos em contacto brevemente.');
        setShowForm(false);
        setSelectedMissao(null);
        setFormData({
          nome: '',
          email: '',
          telefone: '',
          distrito_id: '',
          competencias: [],
          disponibilidade: [],
          motivacao: '',
          aceita_termos: false
        });
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao submeter candidatura');
      }
    } catch (error) {
      toast.error('Erro ao submeter candidatura');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleCompetencia = (comp) => {
    setFormData(prev => ({
      ...prev,
      competencias: prev.competencias.includes(comp)
        ? prev.competencias.filter(c => c !== comp)
        : [...prev.competencias, comp]
    }));
  };

  const toggleDisponibilidade = (disp) => {
    setFormData(prev => ({
      ...prev,
      disponibilidade: prev.disponibilidade.includes(disp)
        ? prev.disponibilidade.filter(d => d !== disp)
        : [...prev.disponibilidade, disp]
    }));
  };

  const filteredMissoes = missoes.filter(m => {
    if (filtroDistrito !== 'all' && m.distrito_id !== filtroDistrito) return false;
    if (filtroTipo !== 'all' && m.tipo_missao !== filtroTipo) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return m.titulo?.toLowerCase().includes(search) ||
             m.descricao?.toLowerCase().includes(search);
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-white" data-testid="public-voluntariado-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/publico" className="flex items-center gap-2">
              <Heart className="w-8 h-8 text-[#0066CC]" fill="#0066CC" />
              <span className="text-xl font-bold text-slate-900">ENTRAJUDATA</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/publico" className="text-slate-600 hover:text-[#0066CC] transition-colors">Início</Link>
              <Link to="/publico/dados-abertos" className="text-slate-600 hover:text-[#0066CC] transition-colors">Dados Abertos</Link>
              <Link to="/publico/instituicoes" className="text-slate-600 hover:text-[#0066CC] transition-colors">Instituições</Link>
              <Link to="/publico/voluntariado" className="text-[#0066CC] font-medium">Ser Voluntário</Link>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/portal/login">
                <Button variant="outline" size="sm">Portal Instituições</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden bg-gradient-to-br from-pink-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <Badge className="bg-pink-100 text-pink-700 mb-4">Voluntariado</Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900">
              Seja um <span className="text-pink-600">Voluntário</span>
            </h1>
            <p className="mt-4 text-xl text-slate-600">
              Junte-se a milhares de pessoas que fazem a diferença na vida de quem mais precisa.
            </p>
            <div className="mt-8 flex gap-4">
              <Button size="lg" className="bg-pink-600 hover:bg-pink-700" onClick={() => setShowForm(true)}>
                <Heart className="w-5 h-5 mr-2" />
                Candidatar-me
              </Button>
              <Button size="lg" variant="outline" onClick={() => document.getElementById('missoes')?.scrollIntoView({ behavior: 'smooth' })}>
                Ver Missões Abertas
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Porquê ser Voluntário?</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            <BenefitCard
              icon={Heart}
              title="Fazer a Diferença"
              description="Impacte diretamente a vida de pessoas que precisam de ajuda"
              color="pink"
            />
            <BenefitCard
              icon={Users}
              title="Nova Comunidade"
              description="Conheça pessoas com os mesmos valores e interesses"
              color="blue"
            />
            <BenefitCard
              icon={Award}
              title="Desenvolvimento"
              description="Adquira novas competências e experiências valiosas"
              color="amber"
            />
            <BenefitCard
              icon={Star}
              title="Certificação"
              description="Receba certificados de participação e formação"
              color="purple"
            />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">O que dizem os nossos voluntários</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, idx) => (
              <Card key={idx} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-pink-600" />
                    </div>
                    <div>
                      <p className="font-semibold">{t.name}</p>
                      <p className="text-sm text-slate-500">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-slate-600 italic">"{t.quote}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Missions */}
      <section id="missoes" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Missões Abertas</h2>
            <p className="mt-2 text-lg text-slate-600">Encontre uma oportunidade de voluntariado perto de si</p>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Pesquisar missões..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filtroDistrito} onValueChange={setFiltroDistrito}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Distrito" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Distritos</SelectItem>
                {distritos.map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Tipos</SelectItem>
                <SelectItem value="Presencial">Presencial</SelectItem>
                <SelectItem value="Remoto">Remoto</SelectItem>
                <SelectItem value="Hibrido">Híbrido</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Mission Cards */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-pink-600" />
            </div>
          ) : filteredMissoes.length === 0 ? (
            <div className="text-center py-12">
              <Heart className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">Não há missões disponíveis com os filtros selecionados</p>
              <Button className="mt-4 bg-pink-600 hover:bg-pink-700" onClick={() => setShowForm(true)}>
                Candidatura Geral
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMissoes.map(missao => (
                <Card key={missao.id} className="hover:shadow-lg transition-shadow group">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <Badge className={
                        missao.tipo_missao === 'Presencial' ? 'bg-green-100 text-green-700' :
                        missao.tipo_missao === 'Remoto' ? 'bg-blue-100 text-blue-700' :
                        'bg-purple-100 text-purple-700'
                      }>
                        {missao.tipo_missao}
                      </Badge>
                      <span className="text-xs text-slate-400">{missao.codigo}</span>
                    </div>
                    <CardTitle className="text-lg group-hover:text-pink-600 transition-colors">
                      {missao.titulo}
                    </CardTitle>
                    <CardDescription className="line-clamp-2">
                      {missao.descricao}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm text-slate-600">
                      {missao.distrito_nome && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span>{missao.distrito_nome}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span>Início: {new Date(missao.data_inicio).toLocaleDateString('pt-PT')}</span>
                      </div>
                      {missao.horario && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span>{missao.horario}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-slate-400" />
                        <span>{missao.voluntarios_confirmados || 0}/{missao.numero_voluntarios} voluntários</span>
                      </div>
                    </div>
                    
                    {missao.competencias_necessarias?.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-1">
                        {missao.competencias_necessarias.slice(0, 3).map(comp => (
                          <Badge key={comp} variant="outline" className="text-xs">{comp}</Badge>
                        ))}
                        {missao.competencias_necessarias.length > 3 && (
                          <Badge variant="outline" className="text-xs">+{missao.competencias_necessarias.length - 3}</Badge>
                        )}
                      </div>
                    )}
                    
                    <Button 
                      className="w-full mt-4 bg-pink-600 hover:bg-pink-700"
                      onClick={() => { setSelectedMissao(missao); setShowForm(true); }}
                    >
                      Candidatar-me
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-pink-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white">Pronto para começar?</h2>
          <p className="mt-4 text-lg text-pink-100">
            Não encontrou uma missão ideal? Faça uma candidatura geral e nós encontramos a oportunidade certa para si.
          </p>
          <Button size="lg" className="mt-8 bg-white text-pink-600 hover:bg-pink-50" onClick={() => setShowForm(true)}>
            <Heart className="w-5 h-5 mr-2" />
            Candidatura Geral
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/publico" className="inline-flex items-center gap-2 mb-4">
            <Heart className="w-6 h-6 text-[#0066CC]" fill="#0066CC" />
            <span className="font-bold">ENTRAJUDATA</span>
          </Link>
          <p className="text-slate-400 text-sm">© {new Date().getFullYear()} ENTRAJUDA. Todos os direitos reservados.</p>
        </div>
      </footer>

      {/* Application Form Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-600" />
              {selectedMissao ? `Candidatura: ${selectedMissao.titulo}` : 'Candidatura de Voluntário'}
            </DialogTitle>
            <DialogDescription>
              Preencha o formulário para submeter a sua candidatura
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome Completo *</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="O seu nome"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@exemplo.pt"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefone *</Label>
                <Input
                  value={formData.telefone}
                  onChange={(e) => setFormData(prev => ({ ...prev, telefone: e.target.value }))}
                  placeholder="912 345 678"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Distrito</Label>
                <Select value={formData.distrito_id} onValueChange={(v) => setFormData(prev => ({ ...prev, distrito_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {distritos.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Competências</Label>
              <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg max-h-[120px] overflow-y-auto">
                {COMPETENCIAS.map(comp => (
                  <button
                    key={comp}
                    type="button"
                    onClick={() => toggleCompetencia(comp)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      formData.competencias.includes(comp)
                        ? 'bg-pink-500 text-white'
                        : 'bg-white border border-slate-200 text-slate-700 hover:border-pink-300'
                    }`}
                  >
                    {comp}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Disponibilidade</Label>
              <div className="grid grid-cols-2 gap-2">
                {DISPONIBILIDADE.map(d => (
                  <div
                    key={d.id}
                    onClick={() => toggleDisponibilidade(d.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.disponibilidade.includes(d.id)
                        ? 'bg-pink-50 border-pink-300'
                        : 'bg-white border-slate-200 hover:border-pink-200'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded border ${
                        formData.disponibilidade.includes(d.id)
                          ? 'bg-pink-500 border-pink-500'
                          : 'border-slate-300'
                      } flex items-center justify-center`}>
                        {formData.disponibilidade.includes(d.id) && (
                          <CheckCircle className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span className="text-sm">{d.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Motivação</Label>
              <Textarea
                value={formData.motivacao}
                onChange={(e) => setFormData(prev => ({ ...prev, motivacao: e.target.value }))}
                placeholder="Conte-nos porque quer ser voluntário..."
                rows={3}
              />
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="termos"
                checked={formData.aceita_termos}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, aceita_termos: checked }))}
              />
              <label htmlFor="termos" className="text-sm text-slate-600 cursor-pointer">
                Li e aceito os <a href="#" className="text-pink-600 underline">Termos e Condições</a> e 
                a <a href="#" className="text-pink-600 underline">Política de Privacidade</a>
              </label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-pink-600 hover:bg-pink-700" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    A enviar...
                  </>
                ) : (
                  <>
                    <Heart className="w-4 h-4 mr-2" />
                    Submeter Candidatura
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BenefitCard({ icon: Icon, title, description, color }) {
  const colorClasses = {
    pink: 'bg-pink-100 text-pink-600',
    blue: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600'
  };

  return (
    <Card className="text-center hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <div className={`w-14 h-14 mx-auto rounded-full ${colorClasses[color]} flex items-center justify-center mb-4`}>
          <Icon className="w-7 h-7" />
        </div>
        <h3 className="font-semibold text-lg mb-2">{title}</h3>
        <p className="text-slate-600 text-sm">{description}</p>
      </CardContent>
    </Card>
  );
}
